import { useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

export type SortColumn =
  | 'advisor'
  | 'status'
  | 'clientAccess'
  | 'startDate'
  | 'otherOrgs'
  | 'subscriptionOwner';

export type SortDirection = 'asc' | 'desc';

interface UseTeamTableStateOptions {
  relationships: AdvisorHierarchyRelationship[] | null;
}

export function useTeamTableState({ relationships }: UseTeamTableStateOptions) {
  const [searchParams, setSearchParams] = useSearchParams();

  // Read state from URL params
  const sortColumn = (searchParams.get('teamSort') as SortColumn) || null;
  const sortDirection = (searchParams.get('teamDir') as SortDirection) || 'asc';
  const searchText = searchParams.get('teamSearch') || '';
  const showAllStatuses = searchParams.get('teamShowAll') === 'true';

  const updateSearchParams = useCallback(
    (updates: Record<string, string | null>) => {
      const newParams = new URLSearchParams(searchParams);
      for (const [key, value] of Object.entries(updates)) {
        if (value === null || value === '') {
          newParams.delete(key);
        } else {
          newParams.set(key, value);
        }
      }
      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const handleSort = useCallback(
    (column: SortColumn) => {
      if (sortColumn === column) {
        // Toggle direction or clear sort
        if (sortDirection === 'asc') {
          updateSearchParams({ teamDir: 'desc' });
        } else {
          // Clear sort on third click
          updateSearchParams({ teamSort: null, teamDir: null });
        }
      } else {
        // New column, start with ascending
        updateSearchParams({ teamSort: column, teamDir: 'asc' });
      }
    },
    [sortColumn, sortDirection, updateSearchParams],
  );

  const handleSearchChange = useCallback(
    (value: string) => {
      updateSearchParams({ teamSearch: value || null });
    },
    [updateSearchParams],
  );

  const handleShowAllStatusesChange = useCallback(
    (value: boolean) => {
      updateSearchParams({ teamShowAll: value ? 'true' : null });
    },
    [updateSearchParams],
  );

  // Filter and sort relationships
  const filteredAndSortedRelationships = useMemo(() => {
    if (!relationships) return null;

    // Filter out inactive and declined statuses by default
    let filtered = relationships;
    if (!showAllStatuses) {
      filtered = filtered.filter(
        r => r.relationshipStatus !== 'inactive' && r.relationshipStatus !== 'declined',
      );
    }

    // Filter by search text
    if (searchText) {
      const lowerSearch = searchText.toLowerCase();
      filtered = filtered.filter(r => {
        const fullName = `${r.advisorFirstName || ''} ${r.advisorLastName || ''}`.toLowerCase();
        const email = (r.advisorEmail || '').toLowerCase();
        const org = (r.organizationDisplayName || r.organizationName || '').toLowerCase();
        return (
          fullName.includes(lowerSearch) || email.includes(lowerSearch) || org.includes(lowerSearch)
        );
      });
    }

    // Sort if a column is selected
    if (!sortColumn) return filtered;

    const sorted = [...filtered].sort((a, b) => {
      let comparison = 0;

      switch (sortColumn) {
        case 'advisor': {
          const aName =
            `${a.advisorLastName || ''} ${a.advisorFirstName || ''} ${a.advisorEmail || ''}`.toLowerCase();
          const bName =
            `${b.advisorLastName || ''} ${b.advisorFirstName || ''} ${b.advisorEmail || ''}`.toLowerCase();
          comparison = aName.localeCompare(bName);
          break;
        }
        case 'status':
          comparison = (a.relationshipStatus || '').localeCompare(b.relationshipStatus || '');
          break;
        case 'clientAccess':
          comparison = (a.enabledClientAccess ? 1 : 0) - (b.enabledClientAccess ? 1 : 0);
          break;
        case 'startDate': {
          const aDate = a.createdDateTime ? new Date(a.createdDateTime).getTime() : 0;
          const bDate = b.createdDateTime ? new Date(b.createdDateTime).getTime() : 0;
          comparison = aDate - bDate;
          break;
        }
        case 'otherOrgs':
          comparison = (a.otherSupervisors || '').localeCompare(b.otherSupervisors || '');
          break;
        case 'subscriptionOwner': {
          const aOwner =
            `${a.subscriptionOwnerLastName || ''} ${a.subscriptionOwnerFirstName || ''}`.toLowerCase();
          const bOwner =
            `${b.subscriptionOwnerLastName || ''} ${b.subscriptionOwnerFirstName || ''}`.toLowerCase();
          comparison = aOwner.localeCompare(bOwner);
          break;
        }
      }

      return sortDirection === 'desc' ? -comparison : comparison;
    });

    return sorted;
  }, [relationships, showAllStatuses, searchText, sortColumn, sortDirection]);

  return {
    // State
    sortColumn,
    sortDirection,
    searchText,
    showAllStatuses,
    filteredAndSortedRelationships,
    // Handlers
    handleSort,
    handleSearchChange,
    handleShowAllStatusesChange,
  };
}
